import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
//   DialogClose,
} from "@workspace/web-ui/components/dialog";
import { Button } from "@workspace/web-ui/components/button";

interface Step<T = any> {
  title?: string;
  content: (formData: T, setFormData: React.Dispatch<React.SetStateAction<T>>) => React.ReactNode;
  validate?: (formData: T) => boolean;
  actions?: React.ReactNode;
}

interface MultiStepDialogProps<T = any> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  steps: Step<T>[];
  initialStep?: number;
  onComplete?: (data: T) => void | Promise<void>;
  showStepIndicators?: boolean;
  finalActions?: React.ReactNode;
  dialogProps?: React.ComponentProps<typeof Dialog>;
  initialData?: T;
}

export function MultiStepDialog<T = any>({
  open,
  onOpenChange,
  steps,
  initialStep = 0,
  onComplete,
  finalActions,
  dialogProps,
  initialData = {} as T,
}: MultiStepDialogProps<T>) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState<T>(initialData);
  const [isSaving, setIsSaving] = useState(false);

  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const step = steps[currentStep];
  const canProceed = step && step.validate ? step.validate(formData) : true;

  const handleNext = async () => {
    if (canProceed) {
      if (!isLastStep) {
        setCurrentStep((prevStep) => prevStep + 1);
      } else if (onComplete) {
        setIsSaving(true);
        try {
          onComplete(formData);
        } finally {
          setIsSaving(false);
        }
      }
    }
  };

  const handleBack = () => {
    if (!isFirstStep) setCurrentStep((s) => s - 1);
  };

  const handleOpenChange = (open: boolean) => {
    setCurrentStep(initialStep);
    setFormData(initialData);
    setIsSaving(false);
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} {...dialogProps}>
      <DialogContent>
        <DialogHeader>
          {step?.title && <DialogTitle>{step.title}</DialogTitle>}
        </DialogHeader>
        {/* {showStepIndicators && steps.length > 1 && (
          <div className="flex justify-center mb-4 gap-2">
            {steps.map((_, idx) => (
              <span
                key={idx}
                className={`inline-block w-2 h-2 rounded-full ${
                  idx === currentStep ? "bg-blue-600" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        )} */}
        <div>{step?.content && step.content(formData, setFormData)}</div>
        <DialogFooter className="mt-6 flex-row flex justify-between gap-2">
          {!isFirstStep ? (
            <Button variant="outline" className="rounded-[10px]" onClick={handleBack} disabled={isSaving}>
              Back
            </Button>
          ) : (
            <span />
          )}
          {step?.actions ? (
            step.actions
          ) : isLastStep ? (
            finalActions || (
              <Button
                className="w-full bg-[#4272DD] rounded-[10px]"
                onClick={handleNext}
                disabled={!canProceed || isSaving}
              >
                {isSaving ? (
                  <span>Saving...</span>
                ) : (
                  "Save changes"
                )}
              </Button>
            )
          ) : (
            <Button
              className="w-full bg-[#4272DD] rounded-[10px]"
              onClick={handleNext}
              disabled={!canProceed || isSaving}
            >
              Next
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 